import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";

import { Rule } from "antd/es/form";
import { useWatch } from "antd/es/form/Form";
import useFormInstance from "antd/es/form/hooks/useFormInstance";
import cn from "classnames";
import { useIntl } from "react-intl";

import { GeocodeResult, GeoManagementState } from "@mapmycustomers/shared/types/base/Located";
import GeoAddress from "@mapmycustomers/shared/types/GeoAddress";
import { Alert, FormItem, Popconfirm, TextField } from "@mapmycustomers/ui";

import ButtonLink from "@app/component/ButtonLink";
import AddressField from "@app/component/input/Address/AddressField";
import { reverseGeocodeAddress } from "@app/store/location/actions";
import geoAddressToAddress from "@app/util/geoAddressToAddress";
import floorToFixed from "@app/util/number/floorToFixed";
import isFloat from "@app/util/number/isFloat";
import useCoordinatesValidation, {
  areCoordinatesValid,
} from "@app/util/validation/useCoordinatesValidation";

import styles from "./AddressFormItem.module.scss";
import messages from "./messages";

interface AddressFormItemProps {
  disabled?: boolean;
  locked?: boolean;
  placeholder?: string;
  required?: boolean;
  rules?: Rule[];
}

const AddressFormItem = ({
  disabled,
  locked,
  onReverseGeocodeAddress,
  placeholder,
  required,
  rules,
}: AddressFormItemProps & typeof mapDispatchToProps) => {
  const intl = useIntl();
  const form = useFormInstance();
  const addressChangedManually = form.isFieldTouched("addressDetails");
  const [open, setOpen] = useState(false);

  const longitude: string | undefined =
    useWatch("longitude", form) ?? form.getFieldValue("longitude"); // Form Item is inside Popover, useWatch gives undefined till popover is opened.
  const latitude: string | undefined = useWatch("latitude", form) ?? form.getFieldValue("latitude");
  const addressDetails: GeoAddress | undefined =
    useWatch("addressDetails", form) ?? form.getFieldValue("addressDetails");

  const [coordinates, setCoordinates] = useState<{
    latitude: string | undefined;
    longitude: string | undefined;
  }>({
    latitude,
    longitude,
  });
  const coordinatesValidation = useCoordinatesValidation(intl, form);
  const formattedLatitude = latitude && isFloat(latitude) ? floorToFixed(Number(latitude), 6) : 0;
  const formattedLongitude =
    longitude && isFloat(longitude) ? floorToFixed(Number(longitude), 6) : 0;

  const disableOkButton = areCoordinatesValid(latitude, longitude);

  const handleConfirm = useCallback(() => {
    setCoordinates({ latitude, longitude });
    onReverseGeocodeAddress({
      callback: ({ address }: GeocodeResult) => {
        const updatedAddress = geoAddressToAddress(address);
        form.setFields([
          {
            name: "addressDetails",
            touched: false,
            value: {
              ...updatedAddress,
              geoAddress: address,
              geoManagementState: GeoManagementState.MANUAL,
            },
          },
        ]);
      },
      coordinates: [formattedLongitude, formattedLatitude],
    });
    setOpen(false);
  }, [form, formattedLatitude, formattedLongitude, latitude, longitude, onReverseGeocodeAddress]);

  const handleCancel = useCallback(() => {
    form.setFields([
      { errors: [], name: "latitude", value: coordinates.latitude },
      { errors: [], name: "longitude", value: coordinates.longitude },
    ]);
    setOpen(false);
  }, [form, coordinates]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open && disableOkButton) {
        handleCancel();
      }
      setOpen(open);
    },
    [disableOkButton, handleCancel]
  );

  useEffect(() => {
    if (addressChangedManually) {
      form.setFieldValue("latitude", null);
      form.setFieldValue("longitude", null);
      form.setFieldValue("addressDetails", {
        ...addressDetails,
        geoAdress: null,
        geoManagementState: GeoManagementState.AUTOMATIC_PRESERVE_ADDRESS,
      });
      setCoordinates({ latitude: undefined, longitude: undefined });
    }
  }, [addressChangedManually, addressDetails, form]);

  const shouldUpdateItem = useCallback(
    (prevValues: any, curValues: any) =>
      prevValues.latitude !== curValues.latitude || prevValues.longitude !== curValues.longitude,
    []
  );

  return (
    <FormItem noStyle shouldUpdate={shouldUpdateItem}>
      <div className={styles.container}>
        <div className={styles.left}>
          <FormItem
            label={intl.formatMessage(messages.address)}
            name="addressDetails"
            required={required}
            rules={rules}
            showLabel={false}
          >
            <AddressField
              className={styles.address}
              disabled={disabled || locked}
              label={intl.formatMessage(messages.address)}
              placeholder={placeholder}
              required={required}
            />
          </FormItem>
          <FormItem hidden name="latitude" />
          <FormItem hidden name="longitude" />
        </div>

        <div className={styles.right}>
          <Popconfirm
            cancelText={intl.formatMessage(messages.cancel)}
            description={
              <div className={styles.coordinates}>
                <Alert message={intl.formatMessage(messages.alert)} showIcon type="info" />
                <FormItem name="latitude" required={required} rules={coordinatesValidation}>
                  <TextField disabled={disabled} label={intl.formatMessage(messages.latitude)} />
                </FormItem>
                <FormItem name="longitude" required={required} rules={coordinatesValidation}>
                  <TextField disabled={disabled} label={intl.formatMessage(messages.longitude)} />
                </FormItem>
              </div>
            }
            icon={null}
            okButtonProps={{ disabled: disableOkButton }}
            okText={intl.formatMessage(messages.ok)}
            onCancel={handleCancel}
            onConfirm={handleConfirm}
            onOpenChange={handleOpenChange}
            open={open}
            overlayClassName={styles.popconfirm}
            placement="bottomRight"
          >
            <ButtonLink
              className={cn(styles.modeBtn, { [styles.disabled]: disabled })}
              disabled={disabled || locked}
            >
              {!latitude && !longitude
                ? intl.formatMessage(messages.useCoordinates)
                : `${formattedLatitude}, ${formattedLongitude}`}
            </ButtonLink>
          </Popconfirm>
        </div>
      </div>
    </FormItem>
  );
};

const mapDispatchToProps = {
  onReverseGeocodeAddress: reverseGeocodeAddress,
};

export default connect(null, mapDispatchToProps)(AddressFormItem);
